import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Container, Offcanvas } from "react-bootstrap";
import { ArrowLeft, ArrowUp } from "react-bootstrap-icons";
import resourcesList from "./resourcesList";
import FadeWrapper from "../FadeWrapper";

const S_COLOR = "#d4af37";

function slugify(text: string) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");
}

export default function ResourcePage() {
  const { id } = useParams();
  const resource = useMemo(
    () => resourcesList.find((r) => r.href === id),
    [id]
  );
  const Article = resource?.mainPageContent || null;
  

  const [showToc, setShowToc] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [btnDisplay, setBtnDisplay] = useState(false);
  const [headings, setHeadings] = useState<
    { id: string; text: string; level: number; top: number }[]
  >([]);
  const [activeId, setActiveId] = useState<string>("");

  const [progress, setProgress] = useState(0);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // collect headings for table of contents
  // for each article page, we use h2,h3 for session headers, so we are collecting these html elements to get titles
  useEffect(() => {
    if (!contentRef.current) return;
    const hEls = Array.from(
      contentRef.current.querySelectorAll("h2, h3")
    ) as HTMLElement[];

    const items = hEls.map((el) => {
      const text = el.textContent || "";
      const id = el.id || slugify(text);
      el.id = id;
      return { id, text, level: el.tagName === "H3" ? 3 : 2, top: el.offsetTop };
    });
    setHeadings(items);
  }, [Article]);

 
  // for archoring tags to specific locations
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.pageYOffset || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setProgress(pct);

      let current = "";
      for (const h of headings) {
        if (scrollTop + 140 >= h.top) current = h.id;
        else break;
      }
      if (current) setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);

  const handleJump = (hid: string) => {
    const el = document.getElementById(hid);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
    setShowToc(false);
  };

  // detect if user scrholl
  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      setBtnDisplay(true);

      if (scrollTimeout) clearTimeout(scrollTimeout);

      // hide button after 0.5s of no scrolling
      scrollTimeout = setTimeout(() => {
        setBtnDisplay(false);
      }, 1500);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) setShowScrollTop(true);
      else setShowScrollTop(false);
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!resource || !Article) return <p className="p-4">404 — Article Not Found</p>;

  return (
    <>
      {/* scroll progress bar at bottom */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          height: 4,
          width: `${progress}%`,
          background: S_COLOR,
          zIndex: 2000,
          transition: "width 0.1s linear",
        }}
      />

      {/* scroll top top button at bottom right */}
      {showScrollTop && (
        <div
          style={{
            position: "fixed",
            bottom: 50,
            right: 40,
          }}
        >
          <Button
            onClick={scrollToTop}
            variant="light"
            className="shadow-sm d-flex align-items-center justify-content-center"
            style={{
              borderRadius: "50%",
              width: 48,
              height: 48,
              backgroundColor: "#1d446b",
            }}
          >
            <ArrowUp size={20} />
          </Button>
        </div>
      )}

      {/* article header , title + read time + date */}
      <div
        className="text-white text-center d-flex align-items-center justify-content-center"
        style={{
          background: "#1d446b",
          minHeight: 160,
        }}
      >
        <div>
          <h1 className="fw-bold mb-2 fs-2">{resource.title}</h1>
          <p className="opacity-75 mb-0">
            {resource.readTime}
          </p>
        </div>
      </div>

      <Container className="py-5" style={{ maxWidth: 1200 }}>
        <Button variant="outline-primary" className="mb-4" href="/resources">
          <ArrowLeft className="me-1" /> Back to resources
        </Button>

        <FadeWrapper>
          <div ref={contentRef} className="article-content">
            <Article />
          </div>
        </FadeWrapper>
      </Container>

      {/* floating table of content button */}
      {btnDisplay && !showToc && (
        <Button
          onClick={() => setShowToc(true)}
          variant="light"
          className="toc-float-btn fw-semibold shadow-sm"
        >
          📜
        </Button>
      )}

      <Offcanvas
        show={showToc}
        onHide={() => setShowToc(false)}
        placement="start"
        scroll
        backdrop
        style={{
          width: 280,
          borderTopRightRadius: "1rem",
          borderBottomRightRadius: "1rem",
          backgroundColor: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(10px)",
          boxShadow: "2px 0 12px rgba(0,0,0,0.15)",
        }}
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title
            className="fw-bold"
            style={{
              color: S_COLOR,
              fontSize: "1rem",
              letterSpacing: "0.05em",
            }}
          >
            TABLE OF CONTENTS
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {headings.length === 0 ? (
            <p className="text-muted">No sections detected.</p>
          ) : (
            <div className="toc-simple">
              {headings.map((h) => (
                <div
                  key={h.id}
                  className={`toc-entry ${activeId === h.id ? "active" : ""}`}
                  onClick={() => handleJump(h.id)}
                >
                  <div className="bar" />
                  <div className="label">{h.text}</div>
                </div>
              ))}
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* styles */}
      <style>{`
        .toc-float-btn {
          position: fixed;
          left: 20px;
          bottom: 40px;   /* Moved from center-left to bottom-left */
          border-radius: 50px;
          border: 2px solid ${S_COLOR};
          color: black;
          background: white;
          z-index: 2001;
          padding: 0.6rem 1.25rem;
          transition: all 0.3s ease;
          animation: float 3s ease-in-out infinite;
        }
        .toc-float-btn:hover {
          background: ${S_COLOR};
          color: white;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .toc-simple {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding-top: 0.5rem;
        }
        .toc-entry {
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .toc-entry .bar {
          width: 4px;
          height: 20px;
          background-color: ${S_COLOR};
          border-radius: 2px;
          margin-right: 12px;
          transition: all 0.2s ease;
        }
        .toc-entry .label {
          font-size: 0.95rem;
          color: #333;
          transition: color 0.2s ease;
        }
        .toc-entry:hover .label {
          color: ${S_COLOR};
        }
        .toc-entry.active .bar {
          height: 28px;
          background-color: ${S_COLOR};
        }
        .toc-entry.active .label {
          color: ${S_COLOR};
          font-weight: 600;
        }

        .article-content {
          font-size: 1.05rem;
          line-height: 1.8;
          color: #212529;
        }
        .article-content h2 {
          margin-top: 2.25rem;
          margin-bottom: 1rem;
          font-weight: 700;
        }
        .article-content h3 {
          margin-top: 1.75rem;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }
        .article-content p { margin-bottom: 1.1rem; }
        .article-content img {
          display: block;
          max-width: 100%;
          height: auto;
          margin: 1.25rem auto;
          border-radius: 0.5rem;
        }
      `}</style>
    </>
  );
}
